import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightLong, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { Gift, Crown, Receipt, ArrowRightLeft, MapPin } from "lucide-react";
import "./SubscriptionPlan.scss";

const plans = [
  {
    key: "promo",
    badge: true,
    badgeVariant: "promo",
    amountLabel: "landing.pricing.promo.amount",
    period: "first_month",
    note: true,
    features: 3,
    popular: false,
    icon: Gift,
  },
  {
    key: "annual",
    badge: true,
    price: "135",
    originalPrice: "150",
    period: "monthly",
    note: true,
    features: 3,
    popular: true,
    icon: Crown,
  },
];

// icons for the reassurance chips built from landing.pricing.footer ("a · b · c")
const FOOTER_ICONS = [Receipt, ArrowRightLeft, MapPin];

const SubscriptionPlan = () => {
  const { t, i18n } = useTranslation();
  // Mobile slider (see .syd-pricing__grid under 576px): the card nearest the strip's center drives the dots
  const gridRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const onGridScroll = () => {
    const el = gridRef.current;
    if (!el) return;
    const center = el.getBoundingClientRect().left + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(el.children).forEach((card, i) => {
      const r = card.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - center);
      if (dist < bestDist) { bestDist = dist; best = i; }
    });
    setActiveIdx(best);
  };

  const goTo = (i) => {
    const el = gridRef.current;
    const card = el?.children[i];
    if (!card) return;
    // scrolls only the strip (scrollIntoView would also move the page vertically)
    const elRect = el.getBoundingClientRect();
    const r = card.getBoundingClientRect();
    el.scrollBy({ left: r.left + r.width / 2 - (elRect.left + elRect.width / 2), behavior: "smooth" });
  };

  return (
    <section id="pricing" className="syd-pricing">
      <div className="syd-pricing__bg" aria-hidden="true">
        <div className="syd-pricing__bg-orb syd-pricing__bg-orb--1" />
        <div className="syd-pricing__bg-orb syd-pricing__bg-orb--2" />
        <div className="syd-pricing__bg-grid" />
      </div>
      <div className="container">
        <motion.div
          className="syd-pricing__header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="syd-pricing__label">{t("landing.nav.pricing")}</div>
          <h2 className="syd-pricing__title">{t("landing.pricing.title")}</h2>
          <span className="syd-pricing__underline" aria-hidden="true" />
          <p className="syd-pricing__sub">
            {t("landing.pricing.sub")}
          </p>
        </motion.div>

        <div className="syd-pricing__grid" ref={gridRef} onScroll={onGridScroll}>
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              className={`syd-plan${plan.popular ? " syd-plan--popular" : ""}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.55 }}
              viewport={{ once: true }}
            >
              {plan.badge && (
                <div className={`syd-plan__badge${plan.badgeVariant ? ` syd-plan__badge--${plan.badgeVariant}` : ""}`}>
                  {t(`landing.pricing.${plan.key}.badge`)}
                </div>
              )}

              <div className="syd-plan__top">
                <div className="syd-plan__head">
                  <span className="syd-plan__icon"><plan.icon size={20} /></span>
                  <div className="syd-plan__name">{t(`landing.pricing.${plan.key}.name`)}</div>
                  {plan.originalPrice && (
                    <span className="syd-plan__save">
                      −{Math.round((1 - Number(plan.price) / Number(plan.originalPrice)) * 100)} %
                    </span>
                  )}
                </div>
                <div className="syd-plan__price">
                  {plan.originalPrice && (
                    <span className="syd-plan__original-amount">{plan.originalPrice} Dhs</span>
                  )}
                  {plan.amountLabel ? (
                    <span className="syd-plan__amount">{t(plan.amountLabel)}</span>
                  ) : (
                    <>
                      <span className="syd-plan__amount">{plan.price}</span>
                      <span className="syd-plan__currency">Dhs</span>
                    </>
                  )}
                  <span className="syd-plan__period">{t(`landing.pricing.period_${plan.period}`)}</span>
                </div>
                {plan.note && (
                  <div className="syd-plan__note">{t(`landing.pricing.${plan.key}.note`)}</div>
                )}
                <p className="syd-plan__desc">{t(`landing.pricing.${plan.key}.desc`)}</p>
              </div>

              <ul className="syd-plan__features">
                {Array.from({ length: plan.features }, (_, j) => j + 1).map((j) => (
                  <li key={j}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {t(`landing.pricing.${plan.key}.feature_${j}`)}
                  </li>
                ))}
              </ul>

              <NavLink
                to={`/${i18n.language}/register`}
                className={`syd-plan__cta${plan.popular ? " syd-plan__cta--primary" : ""}`}
              >
                {t(`landing.pricing.${plan.key}.cta`)}
                <FontAwesomeIcon icon={faArrowRightLong} />
              </NavLink>
            </motion.div>
          ))}
        </div>

        <div className="syd-pricing__dots">
          {plans.map((plan, i) => (
            <button
              key={plan.key}
              type="button"
              className={i === activeIdx ? "is-active" : ""}
              onClick={() => goTo(i)}
              aria-label={t(`landing.pricing.${plan.key}.name`)}
              aria-current={i === activeIdx}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SubscriptionPlan;
